/**
 * Client-side data-question detection — the FIRST node of the analyst
 * routing tree. Non-null routes a chat/voice message straight to the
 * analyst SSE endpoint (with the extracted ticker); null keeps it with the
 * chat model. Lives in @edge/edge-ai so the frontend dispatcher and the
 * admin Router Lab test the SAME regexes — the 2026-08-25 DAIC profile
 * showed a client-detector miss silently costs 20+ seconds.
 *
 * Vocabulary mirrors the server catalogs (why_moved, dilution_risk,
 * financial_history, compare_population, runner history, news). EN + ES.
 */

// NOTE: JS \b is ASCII-only and never matches after accented letters
// (subió\b fails), so word ends use an accent-aware lookahead instead.
export const DATA_RE =
  /\b(why|what|how|did|has|have|is|are|when|cuánt\w*|por qué|qué|cómo)(?![A-Za-zÀ-ÿ]).{0,80}\b(mov\w*|jump\w*|rip\w*|spik\w*|surg\w*|rall\w*|gap\w*|drop\w*|fell|tank\w*|squeez\w*|halt\w*|up|down|news|noticias|headline\w*|catalyst|dilut\w*|diluc\w*|offering|shelf|warrant\w*|runway|float|short interest|revenue|earnings|profit\w*|margin\w*|cagr|burn|cash|eps|ingresos|rentable|ventas|volume|rvol|gapper\w*|runn?\w*|ran(?![A-Za-zÀ-ÿ])|history of|filings?|8-?k|10-?[qk]|sec|subió|cayó|acción)(?![A-Za-zÀ-ÿ])/i

// Workspace-action vocabulary: ALWAYS goes to the chat model (tools that change
// the workspace, panel manipulation, walkthroughs, platform how-to). "draw"
// (draws, drawing), "annotate" and "mark" are here for the late-check fallback
// (2026-10-20): when the server's turn check times out, this regex alone decides
// whether the turn may act. Word-bounded, so "drawdown", "markets" and "bookmark"
// stay market words; "mark-to-market" and "mark to market" are left out by hand.
// The Spanish verbs are the imperative and the infinitive (agrega, añade, abre,
// crea, elimina, quita, borra, dibuja, cambia, cierra). Nouns, first-person and
// future forms stay out on purpose ("cambio", "abierto", "cierre", "cerrará"), and
// so does "creo" in "¿Cómo creo una lista?", which CONCEPT_RE vetoes as a how-to.
// Their end is the accent-aware lookahead, because \b is ASCII-only: it would let
// "cerrará" (will close) match on "cerrar".
export const ACTION_RE =
  /\b(add|open|create|build|make|set|change|switch|navigate|delete|remove|close|move (?:the|this|that|my|these|it|an?)|resize|rename|draw(?:s|ing)?|annotate|mark(?![-\s]+to[-\s]+market)|configure|apply|save|load|back-?test|backtesting|re-?run|simulate|strategy|show me my|walk me through|how do i use|where is the|tutorial|guide me|(?:agregar?|a[ñn]ad(?:e|ir)|abr(?:e|ir)|crear?|eliminar?|quitar?|borrar?|dibujar?|cambiar?|cierra|cerrar)(?![A-Za-zÀ-ÿ]))\b/i

// Setups across many stocks (2026-09-30: "other stocks like this that open
// around $1 and run to $2 in premarket" never reached the analyst because it
// does not start with why/what/how). Mirrors the server's history_scan
// capability heuristic (companion analyst/capabilities/history-scan.ts).
export const HISTORY_SCAN_RE =
  /\b((other|similar) (stocks|tickers|names|setups)|(stocks|tickers|names) (like|similar to) (this|that|it|these)|which (stocks|tickers|names) (have|had|did|opened|gapped|ran|spiked)|(stocks|tickers|names) that (open|opened|gap|gapped|run|ran|spike|spiked|fade|faded|squeeze|squeezed)|find (me )?(stocks|setups|days|tickers) (that|where|with)|otras acciones|acciones (como|similares))/i

/**
 * A Build turn that uses research as input must continue after a terminal
 * analyst result. Ordinary research remains eligible for fast passthrough.
 */
export function isResearchAndActRequest(text: string): boolean {
  return /\b(back-?test|backtesting|re-?run|simulate|strategy)\b/i.test(text)
}

// Learning / platform how-to vocabulary — the chat model owns these too
// (grounded on search_knowledge + walkthroughs + label pointing). Guards
// the detector against Chris's concern (2026-08-25): "how do I…" and
// concept questions must never be shipped to the data analyst.
export const LEARNING_RE =
  /\b(how (do|can|would|should) i\b|how to\b|teach me|learn|show me how|walk( me)? through|what (is|are|does) (a|an|the)\b|what does .{0,30} mean|explain (what|how|the difference)|difference between|where (do|can) i\b|help me (set|get|under)|settings?|colors?|columns?|panel|workspace|layout|scanner setup|configure)\b/i

// Concept questions without an article ("what is short interest") and the
// Spanish how-to / concept vocabulary (2026-09-20). Learn REFUSES market
// questions now, so a concept question that trips the market vocabulary
// ("qué significa RVOL", "what is float") would be refused instead of taught.
// These are the phrasings the replay and spot check caught; the refusal
// telemetry grows the list.
export const CONCEPT_RE =
  /(\bwhat (is|are|'s) (short interest|float|rvol|relative volume|market cap|vwap|atr|eps|cagr|dilution|a?\s?gap ?%?|gap percent|a shelf|an? atm|warrants?|a halt|a catalyst|a runner|a gapper)\b|\b(define|definition of|meaning of)\b|\bqu[eé] (significa|es|son|quiere decir)(?![A-Za-zÀ-ÿ])|\bpara qu[eé] (sirve|es)(?![A-Za-zÀ-ÿ])|\bc[oó]mo (funciona|puedo|se|hago|agrego|cambio|configuro|uso|veo|encuentro|activo|creo)(?![A-Za-zÀ-ÿ])|\bd[oó]nde (veo|encuentro|est[aá]|puedo|se)(?![A-Za-zÀ-ÿ])|\b(expl[ií]came|ens[eé][nñ]ame|ay[uú]dame|mu[eé]strame c[oó]mo)(?![A-Za-zÀ-ÿ])|\b(configuraci[oó]n|columnas?|panel(es)?|gr[aá]fico|dise[nñ]o|ajustes)(?![A-Za-zÀ-ÿ]))/i

// EdgeScript vocabulary (2026-09-07): the language by name, script talk, Pine
// refugees, or the language's own syntax pasted in. The analyst owns
// compile_script and describe_symbol, so this wins ahead of the action and
// learning vetoes, exactly as the server routes it (companion
// analyst/catalogs.ts EDGESCRIPT_RE; keep the two in step). `\bscript\b` does
// not match "transcript".
export const EDGESCRIPT_RE =
  /\b(edgescript|script|scripts|scripting|pine ?script|indicator code|write (me )?(an? )?(custom )?indicator|plot\(|ta\.\w+\(|strategy\.\w+\(|entry long when|@edgescript)\b/i

// The trader's own watchlists (2026-10-20): the analyst reads them and the chat
// model cannot, and a question about them has no market vocabulary for DATA_RE to
// find ("List my watchlists", "My Watchlist (1)", "mis listas de seguimiento").
// Spanish is "mi/mis lista(s)" or "mis watchlists"; "mi lista de seguimiento" and
// "mi lista de favoritos" match on "mi lista". The action and learning vetoes run
// first: "add AAPL to my watchlist" and "agrega AAPL a mi lista" are changes, and
// "how do I see my watchlists" is a how-to ("¿Cómo creo una lista?" is vetoed by
// CONCEPT_RE).
export const WATCHLIST_RE =
  /\b(?:my (?:watch ?lists?|lists?)\b|mis? (?:watch ?lists?|listas?)(?![A-Za-zÀ-ÿ]))/i

// $TSLA or a bare 1-5 letter uppercase token (optionally .X class suffix).
export const CASHTAG_RE = /\$([A-Za-z]{1,5}(?:\.[A-Za-z]{1,2})?)\b/
export const BARE_TICKER_RE = /\b([A-Z]{2,5}(?:\.[A-Z]{1,2})?)\b/

/** Words that look like tickers but never are. */
export const TICKER_STOPLIST = new Set([
  'AI',
  'API',
  'CEO',
  'CFO',
  'ETF',
  'IPO',
  'SEC',
  'FDA',
  'USA',
  'USD',
  'EPS',
  'CAGR',
  'YOY',
  'ATH',
  'ATL',
  // "9:30 AM" is a clock time; Antero Midstream is AM, and $AM still names it
  'AM',
  'PM',
  'AH',
  'OK',
  'ASAP',
  'NYSE',
  'RSI',
  'ATR',
  'RVOL',
  'TTM',
  'GAAP',
  'EBITDA',
  'ELI5',
  'TLDR',
  'MOM',
  'WOW',
  // indicator acronyms that ride along in script questions; only the ones
  // that are not also listed tickers (BB, CCI, KC are real symbols and stay out)
  'SMA',
  'EMA',
  'WMA',
  'HMA',
  'DEMA',
  'TEMA',
  'VWMA',
  'VWAP',
  'MACD',
  'ADX',
  'OBV',
  'BOLL',
  'OHLC',
  'OHLCV',
])

export function extractTicker(text: string): string | undefined {
  const cashtag = text.match(CASHTAG_RE)
  if (cashtag) return cashtag[1]!.toUpperCase()
  for (const match of text.matchAll(new RegExp(BARE_TICKER_RE, 'g'))) {
    const candidate = match[1]!
    if (!TICKER_STOPLIST.has(candidate)) return candidate
  }
  return undefined
}

/**
 * Non-null routes to the analyst; null stays with the chat model.
 * `skipLearningVeto` loosens acceptance for hosts in an analyst-first mode:
 * learning-phrased questions still route to the analyst (action verbs always
 * veto — the chat model owns workspace actions).
 */
export function detectDataQuestion(
  text: string,
  opts?: { skipLearningVeto?: boolean },
): { ticker?: string } | null {
  // script questions first, with or without a ticker, ahead of both vetoes
  if (EDGESCRIPT_RE.test(text)) return { ticker: extractTicker(text) }
  if (ACTION_RE.test(text)) return null
  if (!opts?.skipLearningVeto && (LEARNING_RE.test(text) || CONCEPT_RE.test(text))) return null
  if (WATCHLIST_RE.test(text)) return { ticker: extractTicker(text) }
  if (HISTORY_SCAN_RE.test(text)) return { ticker: extractTicker(text) }
  if (!DATA_RE.test(text)) return null
  return { ticker: extractTicker(text) }
}
